import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { BadgeCheck, Award } from "lucide-react";
import { IMAGES, BIZ } from "../data";
import Reveal from "../components/Reveal";
import { CtaButton } from "../components/Cta";

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section id="sobre" ref={ref} className="relative bg-black py-24 sm:py-32" data-testid="about-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        {/* Image */}
        <Reveal className="order-2 lg:order-1">
          <div className="relative">
            <div className="absolute -left-3 -top-3 h-full w-full border border-[--gold]/40" />
            <motion.div style={{ y: imgY }} className="relative overflow-hidden">
              <img
                src={IMAGES.ducunha}
                alt="Eduardo Cunha — Du Cunha"
                className="aspect-[4/5] w-full object-cover grayscale-[15%]"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              {/* CREF seal */}
              <div className="absolute bottom-4 left-4 flex items-center gap-2 border border-[--gold]/40 bg-black/80 px-4 py-2 backdrop-blur-md">
                <Award className="h-5 w-5 text-[--gold]" />
                <div className="font-display leading-none">
                  <div className="text-xs uppercase tracking-widest text-neutral-400">Registro</div>
                  <div className="text-sm font-bold text-white">{BIZ.cref}</div>
                </div>
              </div>
            </motion.div>
          </div>
        </Reveal>

        {/* Text */}
        <div className="order-1 lg:order-2">
          <Reveal>
            <span className="font-display text-sm font-semibold uppercase tracking-[0.35em] text-[--gold]">
              Sobre o Eduardo
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-6xl">
              18 anos <span className="text-[--gold]">preparando</span> corpos e vidas
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-neutral-300 sm:text-lg">
              Formado em {BIZ.gradYear}, o {BIZ.nickname} construiu quase duas décadas de trabalho com
              ênfase em <span className="text-white">treinamento resistido</span>. A abordagem é sempre
              individual: entender o histórico, respeitar os limites e conduzir cada aluno com técnica e
              proximidade.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                { icon: BadgeCheck, k: `${BIZ.yearsExp} anos`, v: "de experiência" },
                { icon: Award, k: BIZ.gradYear, v: "ano de formação" },
              ].map((s, i) => (
                <div key={i} className="border border-[--gold]/15 bg-[--surface] p-5">
                  <s.icon className="mb-3 h-6 w-6 text-[--gold]" />
                  <div className="font-display text-3xl font-black text-white">{s.k}</div>
                  <div className="text-sm uppercase tracking-widest text-neutral-400">{s.v}</div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-9">
              <CtaButton size="md" variant="outline" testid="about-whatsapp-btn" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
